import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Target } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { animalEmojis, animalNames } from "./constants";
import { Sorteio, analisarBicho, dezenasDoGrupo, nomeGrupo } from "./analiseEngine";
import RankingTable from "./RankingTable";

interface RaioXBichoProps {
  sorteios: Sorteio[];
}

const fmtData = (d: string | null) => {
  if (!d) return "-";
  const [y, m, dd] = d.split("-");
  return `${dd}/${m}/${y.slice(2)}`;
};

const RaioXBicho: React.FC<RaioXBichoProps> = ({ sorteios }) => {
  const [grupo, setGrupo] = useState<number | null>(null);

  const res = useMemo(
    () => (grupo ? analisarBicho(sorteios, grupo) : null),
    [grupo, sorteios]
  );

  const copiar = () => {
    if (!res) return;
    const txt = [
      `Raio-X do Bicho — ${nomeGrupo(res.grupo)}`,
      `Dezena forte: ${res.dezenaForte?.key || "-"}`,
      `Centena provável: ${res.centenaProvavel?.key || "-"}`,
      `Milhar provável: ${res.milharProvavel?.valor || "-"}`,
    ].join("\n");
    navigator.clipboard.writeText(txt);
    toast({ title: "Palpite copiado!", description: "Dezena, centena e milhar do bicho." });
  };

  const baseCurta = sorteios.length < 30;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 border-b border-yellow-500/20 pb-1">
        <p className="text-sm font-bold text-yellow-400 flex items-center gap-2">
          <Target className="h-4 w-4" /> Raio-X do Bicho
        </p>
        <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-[10px]">
          {sorteios.length} sorteios no recorte
        </Badge>
      </div>
      <p className="text-[10px] sm:text-xs text-yellow-200/60">
        Selecione um bicho para ver a dezena mais forte, a centena e a milhar mais prováveis dentro dos
        filtros escolhidos acima.
      </p>

      {/* Grade dos 25 bichos */}
      <div className="grid grid-cols-4 sm:grid-cols-7 lg:grid-cols-13 gap-1.5">
        {Array.from({ length: 25 }, (_, i) => i + 1).map((g) => {
          const active = grupo === g;
          return (
            <button
              key={g}
              onClick={() => setGrupo(active ? null : g)}
              className={`flex flex-col items-center rounded-lg border px-1 py-1.5 transition-colors ${
                active
                  ? "bg-yellow-400 text-black border-yellow-300 font-bold shadow-sm shadow-yellow-500/40"
                  : "bg-black/40 text-yellow-200/80 border-yellow-500/25 hover:bg-yellow-500/10"
              }`}
            >
              <span className="text-base leading-none">{animalEmojis[g]}</span>
              <span className="text-[10px] font-mono leading-tight">{String(g).padStart(2, "0")}</span>
              <span className="text-[9px] leading-tight truncate max-w-full">{animalNames[g]}</span>
            </button>
          );
        })}
      </div>

      {res && (
        <div className="space-y-3">
          {sorteios.length === 0 ? (
            <div className="rounded-lg border border-yellow-500/20 bg-black/30 p-3">
              <p className="text-xs text-yellow-200/70">
                Sem dados no recorte atual. Amplie o período, os prêmios ou a loteria selecionada.
              </p>
            </div>
          ) : (
            <>
              {/* Resumo + palpites */}
              <div className="rounded-xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/10 via-black/40 to-yellow-500/10 p-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-yellow-300">{nomeGrupo(res.grupo)}</p>
                  <Badge
                    variant="outline"
                    className={`text-[9px] ${
                      baseCurta
                        ? "border-orange-400/40 text-orange-300"
                        : "border-emerald-400/40 text-emerald-300"
                    }`}
                  >
                    {baseCurta ? "Base curta — conclusão fraca" : "Base ampla — boa confiança"}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div className="rounded-lg bg-black/40 border border-yellow-500/20 p-2">
                    <p className="text-[9px] text-yellow-200/60">Saiu</p>
                    <p className="text-sm font-bold text-yellow-300">{res.resumo?.freq ?? 0}x</p>
                    <p className="text-[9px] text-yellow-200/50">
                      {(res.resumo?.pct ?? 0).toFixed(1)}% das saídas
                    </p>
                  </div>
                  <div className="rounded-lg bg-black/40 border border-yellow-500/20 p-2">
                    <p className="text-[9px] text-yellow-200/60">Atraso atual</p>
                    <p className="text-sm font-bold text-yellow-300">{res.resumo?.atraso ?? "-"}</p>
                    <p className="text-[9px] text-yellow-200/50">
                      recorde {res.resumo?.maiorAtraso ?? "-"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-black/40 border border-yellow-500/20 p-2">
                    <p className="text-[9px] text-yellow-200/60">Última vez</p>
                    <p className="text-sm font-bold text-yellow-300">{fmtData(res.resumo?.ultimaData ?? null)}</p>
                    <p className="text-[9px] text-yellow-200/50">{res.resumo?.ultimoTurno || "-"}</p>
                  </div>
                  <div className="rounded-lg bg-black/40 border border-yellow-500/20 p-2">
                    <p className="text-[9px] text-yellow-200/60">Dezenas do grupo</p>
                    <p className="text-[11px] font-mono font-bold text-yellow-300">
                      {dezenasDoGrupo(res.grupo).join(" ")}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="rounded-lg bg-black/50 border border-yellow-400/40 p-2 text-center">
                    <p className="text-[9px] text-yellow-200/60">Dezena mais forte</p>
                    <p className="text-2xl font-mono font-black text-yellow-400">
                      {res.dezenaForte?.key || "--"}
                    </p>
                    <p className="text-[9px] text-yellow-200/50">
                      {res.dezenaForte ? `${res.dezenaForte.freq}x • atraso ${res.dezenaForte.atraso}` : "sem dados"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-black/50 border border-yellow-400/40 p-2 text-center">
                    <p className="text-[9px] text-yellow-200/60">Centena mais provável</p>
                    <p className="text-2xl font-mono font-black text-yellow-400">
                      {res.centenaProvavel?.key || "---"}
                    </p>
                    <p className="text-[9px] text-yellow-200/50">
                      {res.centenaProvavel
                        ? `${res.centenaProvavel.freq}x • atraso ${res.centenaProvavel.atraso}`
                        : "sem dados"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-black/50 border border-yellow-400/40 p-2 text-center">
                    <p className="text-[9px] text-yellow-200/60">Milhar combinada provável</p>
                    <p className="text-2xl font-mono font-black text-yellow-400">
                      {res.milharProvavel?.valor || "----"}
                    </p>
                    <p className="text-[9px] text-yellow-200/50">
                      {res.milharProvavel
                        ? res.milharProvavel.combinada
                          ? "sugestão combinada (centena líder)"
                          : `${res.milharProvavel.freq}x no recorte`
                        : "sem dados"}
                    </p>
                  </div>
                </div>

                <Button
                  size="sm"
                  onClick={copiar}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar palpite do bicho
                </Button>
              </div>

              {/* Rankings detalhados */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                <RankingTable titulo="🔢 Dezenas do bicho" itens={res.dezenas} limite={4} destaque="freq" />
                <RankingTable titulo="💯 Centenas do bicho" itens={res.centenas} limite={10} destaque="freq" />
                <RankingTable titulo="🎯 Milhares do bicho" itens={res.milhares} limite={10} destaque="freq" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RaioXBicho;
